// UI strings for the notebook tasks ("Vihkoon" in Tänään): handwritten tasks for a paper
// notebook. Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md (sinä-muoto, no internal jargon, "1 sana", "2 sanaa").
import { defineArea } from '../area';

const fiTimes = (n: number) => (n === 2 ? 'kaksi kertaa' : n === 3 ? 'kolme kertaa' : `${n} kertaa`);
const enTimes = (n: number) => (n === 2 ? 'twice' : n === 3 ? 'three times' : `${n} times`);
/** "On <weekday>s", by dayOfWeek (0 = Sunday): the lessons' weekday (engine lessonWeekday). */
const fiOnDays = ['Sunnuntaisin', 'Maanantaisin', 'Tiistaisin', 'Keskiviikkoisin', 'Torstaisin', 'Perjantaisin', 'Lauantaisin'];
const enOnDays = ['On Sundays', 'On Mondays', 'On Tuesdays', 'On Wednesdays', 'On Thursdays', 'On Fridays', 'On Saturdays'];

export const notebookFi = {
  title: 'Vihkoon',
  print: 'Tulosta',
  kinds: {
    copy: 'Kopioi',
    table: 'Taivutustaulu',
    translate: 'Käännä vihkoon',
    dictation: 'Sanelu vihkoon',
    letter: 'Kirje vihkoon',
  },
  copyNew: (times: number) => `Kirjoita tämän päivän uudet sanat vihkoon, jokainen ${fiTimes(times)}.`,
  copyDue: (times: number) => `Kirjoita tänään kerratut sanat vihkoon, jokainen ${fiTimes(times)}.`,
  tableAsk: 'Kirjoita taulukko vihkoon muistista ja tarkista vasta sitten.',
  /** "preesens, indikatiivi, aktiivi" before a verb table. */
  verbForms: (what: string) => `Verbin muodot: ${what}.`,
  translateAsk: 'Käännä lauseet valyriaksi vihkoon. Tarkista sitten jokainen ja merkitse, miten meni.',
  dictationAsk: 'Kuuntele ja kirjoita kuulemasi vihkoon valyriaksi. Tarkista sitten ja merkitse, miten meni.',
  letterAsk: 'Kirjoita vihkoon lyhyt kirje kumppanillesi valyriaksi.',
  reveal: 'Näytä vastaus',
  hide: 'Piilota vastaus',
  done: 'Valmis',
  doneAt: (time: string) => `Tehty klo ${time}`,
  howDidItGo: 'Miten meni?',
  marks: { right: 'Oikein', close: 'Melkein', wrong: 'Väärin' },
  marked: (right: number, n: number) => `${right}/${n} oikein`,
  /** What a self-mark does, under the marks (said once per task). */
  practiceTag: 'harjoituslause',
  practiceNote: 'oppitunnin lause, ei Petersonin',
  wiki: 'Petersonin wiki',
  /** The English text of an example, when the Finnish UI has no Finnish for it. */
  inEnglish: 'englanniksi',
  peterson: 'Petersonin äänite',
  partnerTake: (name: string) => `Äänitys: ${name}`,
  partnerTakeFallback: 'Kumppanisi äänitys',
  play: 'Kuuntele',
  pause: 'Tauko',
  emptyCell: 'tyhjä ruutu',
  letter: {
    words: 'Käytä ainakin kahta näistä sanoista:',
    grammar: (what: string) => `Käytä tämän viikon kielioppia: ${what}.`,
    topic: (n: number, title: string) => `Kirjoita oppitunnin ${n} aiheesta: ${title}.`,
    personal: ['Kerro, mitä teit tänään.', 'Kysy kumppaniltasi jotakin.', 'Kerro, mitä toivot ensi viikolta.', 'Kerro jostakin, mistä pidät.'],
    toLetters: 'Kirjoita se myös Kirjeisiin',
  },
  setting: {
    label: 'Vihkotehtäviä päivässä',
    /** `weekday`: the day lessons open on now (dayOfWeek, 0 = Sunday), the day of the week's letter. */
    example: (n: number, weekday: number) =>
      n === 0
        ? '0: Tänään-sivulla ei näy vihkotehtäviä.'
        : n === 1
          ? '1: päivän korttien jälkeen yksi tehtävä paperivihkoon, esimerkiksi päivän uudet sanat kolmeen kertaan.'
          : `${n}: päivän korttien jälkeen ${n === 2 ? 'kaksi' : 'kolme'} tehtävää paperivihkoon, esimerkiksi päivän uudet sanat kolmeen kertaan ja yhden sanan taivutustaulu muistista. ${fiOnDays[weekday]} eli oppituntipäivänä yksi niistä on viikon kirje kumppanillesi.`,
  },
};

export const notebookEn: typeof notebookFi = {
  title: 'In your notebook',
  print: 'Print',
  kinds: {
    copy: 'Copy',
    table: 'Inflection table',
    translate: 'Translate in your notebook',
    dictation: 'Dictation',
    letter: 'A letter in your notebook',
  },
  copyNew: (times: number) => `Write today’s new words in your notebook, each one ${enTimes(times)}.`,
  copyDue: (times: number) => `Write the words you reviewed today in your notebook, each one ${enTimes(times)}.`,
  tableAsk: 'Write the table in your notebook from memory, then check it.',
  verbForms: (what: string) => `Verb forms: ${what}.`,
  translateAsk: 'Translate the sentences into High Valyrian in your notebook. Then check each one and mark how it went.',
  dictationAsk: 'Listen and write what you hear in High Valyrian in your notebook. Then check it and mark how it went.',
  letterAsk: 'Write a short letter to your partner in High Valyrian in your notebook.',
  reveal: 'Show answer',
  hide: 'Hide answer',
  done: 'Done',
  doneAt: (time: string) => `Done at ${time}`,
  howDidItGo: 'How did it go?',
  marks: { right: 'Right', close: 'Almost', wrong: 'Wrong' },
  marked: (right: number, n: number) => `${right}/${n} right`,
  practiceTag: 'practice sentence',
  practiceNote: 'from a lesson, not Peterson’s',
  wiki: 'Peterson’s wiki',
  inEnglish: 'in English',
  peterson: 'Peterson’s recording',
  partnerTake: (name: string) => `Recorded by ${name}`,
  partnerTakeFallback: 'Your partner’s recording',
  play: 'Play',
  pause: 'Pause',
  emptyCell: 'empty cell',
  letter: {
    words: 'Use at least two of these words:',
    grammar: (what: string) => `Use this week’s grammar: ${what}.`,
    topic: (n: number, title: string) => `Write about the topic of lesson ${n}: ${title}.`,
    personal: ['Tell what you did today.', 'Ask your partner something.', 'Tell what you hope for next week.', 'Tell about something you like.'],
    toLetters: 'Write it in Letters too',
  },
  setting: {
    label: 'Notebook tasks a day',
    example: (n: number, weekday: number) =>
      n === 0
        ? '0: Today shows no notebook tasks.'
        : n === 1
          ? '1: after the day’s cards, one task for your paper notebook, for example today’s new words three times each.'
          : `${n}: after the day’s cards, ${n === 2 ? 'two' : 'three'} tasks for your paper notebook, for example today’s new words three times each and one word’s inflection table from memory. ${enOnDays[weekday]}, your lesson day, one of them is the week’s letter to your partner.`,
  },
};

// Messages.notebook (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('notebook', notebookFi, notebookEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof notebookFi> {}
}
